import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Inject,
  OnDestroy,
  OnInit,
  Optional,
  PLATFORM_ID,
  Renderer2,
  DOCUMENT,
} from '@angular/core';
import { RouterModule, RouterOutlet } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { AnalyticsService } from './services/analytics.service';
import { SettingsService } from './services/settings.service';
import { MenuComponent } from './parts/menu/menu.component';
import { FooterComponent } from './parts/footer/footer.component';
import { isPlatformBrowser } from '@angular/common';
import { CookieConsentService } from './services/cookie-consent.service';
import { CookieConsentModel } from './models/cookie-consent.model';
import { appSettings } from './app.settings';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    MenuComponent,
    FooterComponent,
    RouterModule,
    FormsModule,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  settingsModel = appSettings();
  loading: boolean = true;

  mobile: boolean = false;
  tablet: boolean = false;

  gtag: Function = new Function();

  cookieConsentShow = computed(() => this.cookieConsentService.showConsent());

  consent: CookieConsentModel = this.cookieConsentService.consent();

  hasScrolled = false;

  // Expansion panel state
  isExpansionPanelOpen = false;

  private destroyFns: Array<() => void> = [];
  listenerRemoved = false;

  private platformId = inject(PLATFORM_ID);
  private document = inject(DOCUMENT);
  private elementRef = inject(ElementRef);

  constructor(
    private settingsService: SettingsService,
    private analyticsService: AnalyticsService,
    private cookieConsentService: CookieConsentService,

    @Inject(PLATFORM_ID) private platformID: string,
    private renderer2: Renderer2,
    @Optional() @Inject('ISMOBILE') isMobile: boolean,
    @Optional() @Inject('ISTABLET') isTablet: boolean,
  ) {
    this.settingsService.isMobile.set(isMobile);
    this.settingsService.isTablet.set(isTablet);

    this.settingsService.setMobile(isMobile ?? false);

    this.mobile = isMobile ?? false;
    this.tablet = isTablet ?? false;

    effect(() => {
      this.checkForCookieConsents();
    });
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.listenToBody('click');
      this.listenToBody('scroll');

      this.analyticsService.addTagManager(this.renderer2);
    }
  }

  ngOnInit() {
    // this.isMobile = this.settingsService.getMobileStatus();
    // this.isTablet = this.settingsService.getTabletStatus();
    this.settingsService.getSettings();
  }

  ngOnDestroy() {
    this.destroyFns.forEach((fn) => fn());
  }

  private listenToBody(eventName: string) {
    if (!this.cookieConsentShow()) {
      this.cookieConsentService.updateConsent(this.consent, 'setAllCookies');
    }

    const target = eventName === 'scroll' ? 'window' : 'body';

    const destroy = this.renderer2.listen(target, eventName, () => {
      if (this.listenerRemoved) return;

      if (eventName === 'scroll' && !this.hasScrolled) {
        this.hasScrolled = true;
        return; // Ignore the first scroll on page load
      }

      if (!this.cookieConsentShow()) {
        this.checkForCookieConsents();
        this.listenerRemoved = true;

        this.destroyFns.forEach((fn) => fn());
        this.destroyFns = [];
      }

      this.hasScrolled = true;
    });

    this.destroyFns.push(destroy);
  }

  checkForCookieConsents() {
    if (isPlatformBrowser(this.platformId)) {
      if (this.cookieConsentService.showConsent()) {
        document.querySelector('body')?.classList.add('cookie-consent-active');
      } else {
        document
          .querySelector('body')
          ?.classList.remove('cookie-consent-active');
      }
    }
  }

  setAllCookies(setAll: boolean) {
    if (setAll) {
      this.consent.Functional = true;
      this.consent.Analytics = true;
      this.consent.Marketing = true;
    }

    this.cookieConsentService.updateConsent(this.consent, 'setAllCookies');

    this.document
      .querySelector('body')
      ?.classList.remove('cookie-consent-active');
  }

  setUpAnalytics() {
    this.cookieConsentService.loadTagManagerScript(
      'GTM-PN42DG3N',
      this.renderer2,
      this.elementRef,
    );
  }

  declineAllCookies() {
    this.consent.Functional = false;
    this.consent.Analytics = false;
    this.consent.Marketing = false;

    this.cookieConsentService.updateConsent(this.consent, 'declineAllCookies');

    this.document
      .querySelector('body')
      ?.classList.remove('cookie-consent-active');
  }

  updateConsent() {
    this.cookieConsentService.updateConsent(this.consent);
  }

  // Toggle expansion panel visibility
  toggleExpansionPanel() {
    this.isExpansionPanelOpen = !this.isExpansionPanelOpen;
  }
}
